import { Alert, Button, TextField, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import AppDialog from "components/common/AppDialog";

import { confirmPhraseMatches } from "./deleteConfirm";

interface DeleteJobConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  busy?: boolean;
  /** Why the last attempt failed. The dialog stays open so the reason is read. */
  error?: string | null;
  /** When set, Delete stays disabled until this exact phrase is typed. */
  confirmPhrase?: string;
  /** Replaces the paragraph on what leaves S3 (null drops it): a split job's
   * S3 data is its workspace, not logs and rosters. */
  detail?: ReactNode | null;
  /** Controls under the message (what else to delete). */
  extra?: ReactNode;
}

/**
 * Shared confirmation for every job-delete action in Bots Management. A job's
 * own S3 data - its log archives, the error screenshots of its tasks and its
 * roster and matches files - always goes with it: a job row without its logs is
 * a job nobody can read. The folder is shared with other jobs of the same
 * document type, so a roster file another job still reads, browser profiles and
 * the documents themselves stay.
 */
export default function DeleteJobConfirmDialog({
  open,
  title,
  message,
  onCancel,
  onConfirm,
  confirmDisabled = false,
  busy = false,
  error = null,
  confirmPhrase,
  detail,
  extra,
}: DeleteJobConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  // What was typed for one target never carries over to the next.
  useEffect(() => {
    if (open) setTyped("");
  }, [open, confirmPhrase]);
  const phraseTyped = confirmPhrase == null || confirmPhraseMatches(typed, confirmPhrase);

  return (
    <AppDialog
      open={open}
      onClose={() => !busy && onCancel()}
      title={title}
      actions={
        <>
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={confirmDisabled || busy || !phraseTyped}
            onClick={onConfirm}
          >
            Delete
          </Button>
        </>
      }
    >
      <Typography variant="body2">{message}</Typography>
      {detail === undefined ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The S3 data of the deleted job(s) is removed too: the log archives and error
          screenshots of their tasks, removed tasks included, and their roster and matches
          files. The logs are the only record of which employees were processed, so a new
          job over the same folder starts from scratch. A roster file another job still
          reads, browser profiles and the documents themselves stay.
        </Typography>
      ) : detail ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {detail}
        </Typography>
      ) : null}
      {extra}
      {confirmPhrase != null ? (
        <TextField
          autoFocus
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          label={`Type "${confirmPhrase}" to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={busy}
          inputProps={{ autoComplete: "off", spellCheck: false }}
        />
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
    </AppDialog>
  );
}
